import { normalizeRatios } from "./normalize";
import { activePane, allGroups, findNode, findPane } from "./tree";
import type { LayoutNode, LayoutTree, NavDirection, Rect, TabGroup } from "./types";

/**
 * Pixel rects for every split, tab group, and pane (a pane's rect is its group's rect) when the
 * tree fills a `width` x `height` box with `gutter` pixels between split children.
 */
export function layoutRects(
  tree: LayoutTree,
  width: number,
  height: number,
  gutter = 4,
): Map<string, Rect> {
  const rects = new Map<string, Rect>();
  const visit = (node: LayoutNode, rect: Rect): void => {
    rects.set(node.id, rect);
    if (node.type === "tabs") {
      for (const tab of node.tabs) rects.set(tab.id, rect);
      return;
    }
    const count = node.children.length;
    const ratios = normalizeRatios(node.ratios, count);
    const horizontal = node.direction === "horizontal";
    const available = Math.max(0, (horizontal ? rect.width : rect.height) - gutter * (count - 1));
    let offset = horizontal ? rect.x : rect.y;
    node.children.forEach((child, i) => {
      const size = available * (ratios[i] ?? 0);
      visit(
        child,
        horizontal
          ? { x: offset, y: rect.y, width: size, height: rect.height }
          : { x: rect.x, y: offset, width: rect.width, height: size },
      );
      offset += size + gutter;
    });
  };
  visit(tree.root, { x: 0, y: 0, width, height });
  return rects;
}

const EPS = 1e-9;

function overlap(a0: number, aLen: number, b0: number, bLen: number): number {
  return Math.min(a0 + aLen, b0 + bLen) - Math.max(a0, b0);
}

type Score = [overlaps: number, distance: number, crossDistance: number];

function score(src: Rect, dst: Rect, direction: NavDirection): Score | undefined {
  const dx = dst.x + dst.width / 2 - (src.x + src.width / 2);
  const dy = dst.y + dst.height / 2 - (src.y + src.height / 2);
  let distance: number;
  let side: number;
  let cross: number;
  switch (direction) {
    case "left":
      distance = src.x - (dst.x + dst.width);
      side = overlap(src.y, src.height, dst.y, dst.height);
      cross = Math.abs(dy);
      break;
    case "right":
      distance = dst.x - (src.x + src.width);
      side = overlap(src.y, src.height, dst.y, dst.height);
      cross = Math.abs(dy);
      break;
    case "up":
      distance = src.y - (dst.y + dst.height);
      side = overlap(src.x, src.width, dst.x, dst.width);
      cross = Math.abs(dx);
      break;
    case "down":
      distance = dst.y - (src.y + src.height);
      side = overlap(src.x, src.width, dst.x, dst.width);
      cross = Math.abs(dx);
      break;
  }
  if (distance < -EPS) return undefined;
  return [side > EPS ? 0 : 1, distance, cross];
}

function better(a: Score, b: Score): boolean {
  for (let i = 0; i < a.length; i++) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (Math.abs(x - y) > EPS) return x < y;
  }
  return false;
}

/**
 * The pane the user reaches by moving in `direction` from `paneId`: the active tab of the
 * geometrically nearest group on that side, preferring groups that overlap the source on the
 * cross axis. Undefined at an edge.
 */
export function neighbourPane(
  tree: LayoutTree,
  paneId: string,
  direction: NavDirection,
): string | undefined {
  const found = findPane(tree, paneId);
  if (!found) return undefined;
  const rects = layoutRects(tree, 1, 1, 0);
  const src = rects.get(found.group.id);
  if (!src) return undefined;
  let best: { score: Score; group: TabGroup } | undefined;
  for (const group of allGroups(tree)) {
    if (group.id === found.group.id || group.tabs.length === 0) continue;
    const rect = rects.get(group.id);
    if (!rect) continue;
    const s = score(src, rect, direction);
    if (s && (!best || better(s, best.score))) best = { score: s, group };
  }
  return best ? activePane(best.group)?.id : undefined;
}

function tabOffset(tree: LayoutTree, groupId: string, offset: number): string | undefined {
  const group = findNode(tree, groupId);
  if (!group || group.type !== "tabs" || group.tabs.length === 0) return undefined;
  const active = activePane(group);
  const index = active ? group.tabs.indexOf(active) : 0;
  const count = group.tabs.length;
  return group.tabs[(((index + offset) % count) + count) % count]?.id;
}

/** The tab after the group's active tab, wrapping around. */
export function nextTab(tree: LayoutTree, groupId: string): string | undefined {
  return tabOffset(tree, groupId, 1);
}

/** The tab before the group's active tab, wrapping around. */
export function prevTab(tree: LayoutTree, groupId: string): string | undefined {
  return tabOffset(tree, groupId, -1);
}
